/* /app/chainage — module Chaînage.
 * Cartographie des causalités (up = pourquoi) et d'exécution (down = comment).
 * MVP : liste de chaînes, éditeur de chaîne avec graphe React-Flow, import
 * depuis Problem Gravity + Utility Mapping, lecture IA NOAM (levier le plus
 * puissant).
 */
import { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { notify, fmtError } from "@/lib/notify";
import AiText from "@/components/AiText";
import { useTranslation } from "react-i18next";
import DocumentAttachZone from "@/components/DocumentAttachZone";
import ModuleHelpButton from "@/components/ModuleHelpButton";
import InlineGuide from "@/components/InlineGuide";
import { PersonaAction } from "@/lib/persona";
import {
  ReactFlow, Background, Controls, MarkerType,
  useNodesState, useEdgesState, addEdge,
  Handle, Position,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import {
  Plus, X, Trash2, Loader2, GitBranch, ChevronLeft, Sparkles, Link as LinkIcon,
  ArrowUpToLine, ArrowDownToLine, FileText, Check, ShieldCheck, AlertTriangle, XCircle,
  Network, Rows, BarChart3, RefreshCw, Target,
} from "lucide-react";
import ThreeQPageHeader from "@/components/ThreeQPageHeader";

const NODE_TYPES = ["cause", "intention", "dependance", "action", "ressource", "mesure"];
const LINK_TYPES = ["cause_effet", "intention_action", "dependance_impact", "neutre"];

const TYPE_COLORS = {
  cause: "#E24B4A",
  intention: "#7F77DD",
  dependance: "#EF9F27",
  action: "#1D9E75",
  ressource: "#378ADD",
  mesure: "#A1A1AA",
};

const TYPE_LABELS_FR = {
  cause: "Cause",
  intention: "Intention",
  dependance: "Dépendance",
  action: "Action",
  ressource: "Ressource",
  mesure: "Mesure",
};

// Couleur des arêtes selon la force du lien (1-10)
function edgeColorByStrength(s) {
  if (s >= 7) return "#1D9E75";   // vert fort
  if (s >= 4) return "#EF9F27";   // orange moyen
  return "#E24B4A";               // rouge faible
}

// Format compact d'un montant en M€ → "2.4 Mrd" / "320 M" / etc.
function fmtMeur(v) {
  if (v == null || isNaN(v)) return "—";
  const n = Number(v);
  if (n >= 1000) return `${(n / 1000).toFixed(n >= 10000 ? 0 : 1)} Mrd`;
  if (n >= 1) return `${n.toFixed(n >= 10 ? 0 : 1)} M`;
  return `${(n * 1000).toFixed(0)} k`;
}

// ---------------- Custom node: inline editable title + market badge ----------------
function EditableNode({ id, data }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(data.label || "");
  useEffect(() => { setValue(data.label || ""); }, [data.label]);
  const commit = () => {
    setEditing(false);
    const v = value.trim();
    if (v && v !== data.label && typeof data.onRename === "function") {
      data.onRename(id, v);
    } else {
      setValue(data.label || "");
    }
  };
  const bg = TYPE_COLORS[data.type] || "#378ADD";
  const isFocal = !!data.isFocal;
  const market = data.market; // { local:{value_meur,label}, capture_pct, capture_value_meur_annual, narrative }
  // V23.14.9 — Heatmap value : bordure dorée + glow proportionnel à la capture annuelle
  const heat = Math.min(1, Math.max(0, data.heatIntensity || 0));
  const heatBorder = heat > 0.05
    ? `rgba(239, 209, 39, ${(0.35 + 0.55 * heat).toFixed(2)})`
    : null;
  const heatGlow = heat > 0.05
    ? `0 0 ${Math.round(8 + 18 * heat)}px rgba(239, 209, 39, ${(0.25 + 0.45 * heat).toFixed(2)})`
    : null;
  const baseShadow = isFocal
    ? "0 0 0 4px rgba(55,138,221,0.35), 0 6px 20px rgba(0,0,0,0.4)"
    : "0 4px 14px rgba(0,0,0,0.3)";
  return (
    <div
      onDoubleClick={(e) => { e.stopPropagation(); setEditing(true); }}
      style={{
        background: `linear-gradient(135deg, ${bg} 0%, ${bg}CC 100%)`,
        color: "white",
        border: isFocal
          ? "3px solid #ffffff"
          : (heatBorder ? `2px solid ${heatBorder}` : "1px solid rgba(255,255,255,0.18)"),
        boxShadow: heatGlow ? `${baseShadow}, ${heatGlow}` : baseShadow,
        borderRadius: 12,
        padding: isFocal ? "12px 14px" : "10px 12px",
        minWidth: isFocal ? 240 : 200,
        maxWidth: 260,
        fontSize: 12,
        position: "relative",
      }}
      data-testid={`chain-node-${id}`}
      data-heat={heat.toFixed(2)}
    >
      <Handle type="target" position={Position.Top} style={{ background: "#fff", width: 8, height: 8 }} />
      {/* Header : type + niveau */}
      <div className="flex items-center justify-between gap-1.5 mb-1 text-[9px] uppercase tracking-wider font-bold opacity-90">
        <span>{TYPE_LABELS_FR[data.type] || data.type}</span>
        <span className="px-1.5 py-0.5 rounded bg-black/30 text-white">N{data.level ?? 1}</span>
      </div>
      {/* Titre */}
      {editing ? (
        <input
          autoFocus
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === "Enter") commit();
            if (e.key === "Escape") { setEditing(false); setValue(data.label || ""); }
          }}
          className="w-full bg-white/20 border border-white/40 text-white text-[13px] font-semibold px-1.5 py-0.5 rounded outline-none"
          data-testid={`chain-node-input-${id}`}
        />
      ) : (
        <div
          className="cursor-text font-semibold leading-tight"
          style={{ fontSize: isFocal ? 14 : 13 }}
          title="Double-clic pour renommer"
        >
          {data.label}
        </div>
      )}
      {/* Market badge (si calculé) */}
      {market && market.is_market_relevant && market.local?.value_meur != null && (
        <div className="mt-2 pt-2 border-t border-white/20 text-[10px] leading-tight space-y-0.5">
          <div className="flex items-center gap-1 font-semibold">
            <span>📊</span>
            <span>{fmtMeur(market.local.value_meur)} {market.currency_primary || "CHF"}</span>
            <span className="opacity-70">· {market.local.label || "local"}</span>
          </div>
          {market.capture_pct != null && (
            <div className="opacity-90">
              Capture {market.capture_pct}% → <strong>{market.capture_value_meur_annual != null ? `${fmtMeur(market.capture_value_meur_annual)} ${market.currency_primary || "CHF"}/an` : "—"}</strong>
            </div>
          )}
        </div>
      )}
      <Handle type="source" position={Position.Bottom} style={{ background: "#fff", width: 8, height: 8 }} />
    </div>
  );
}

const nodeTypes = { editable: EditableNode };

export default function Chainage() {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeId, setActiveId] = useState(null);
  const [creating, setCreating] = useState(false);
  const [autoGenBusy, setAutoGenBusy] = useState(false); // V23.16.12 — Auto-gen NOAM
  // V22.5 P0 fix — Consommation des params URL `?prefill_opp=<id>` et `?prefill_level=<n>`
  // émis par OriginationExtensions.jsx (boutons "Chaînage" du Friction Gap).
  // Affiche un bandeau d'info + ouvre auto la modal "Nouvelle chaîne" préremplie.
  const [searchParams, setSearchParams] = useSearchParams();
  const [prefillBanner, setPrefillBanner] = useState(null);
  const [prefillData, setPrefillData] = useState(null); // {title, description}
  const prefillConsumedRef = useRef(false);

  const load = async () => {
    setLoading(true);
    try { const r = await api.get("/workspace/chains"); setItems(r.data.items || []); }
    catch (e) { notify.error(fmtError(e)); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  // V22.5 — Lecture des params URL au mount (une seule fois).
  useEffect(() => {
    if (prefillConsumedRef.current) return;
    const prefillOpp = searchParams.get("prefill_opp");
    const prefillLevel = searchParams.get("prefill_level");
    if (!prefillOpp && !prefillLevel) return;
    prefillConsumedRef.current = true;
    (async () => {
      if (prefillOpp) {
        try {
          const r = await api.get("/workspace/origination/pipeline");
          const allOpps = Object.values(r.data?.by_step || {}).flat();
          const opp = allOpps.find((o) => o.id === prefillOpp);
          if (opp) {
            setPrefillBanner({ opp_id: opp.id, opp_titre: opp.titre, opp_secteur: opp.secteur || "" });
            setPrefillData({
              title: `Chaînage — ${opp.titre}`,
              description: `Chaînage causal généré depuis l'opportunité « ${opp.titre} »${opp.secteur ? ` (secteur ${opp.secteur})` : ""}.`,
            });
            setCreating(true);
            notify.success(`Opportunité « ${opp.titre} » reçue depuis le Friction Gap.`);
          } else {
            setPrefillBanner({ opp_titre: "Opportunité introuvable" });
            notify.error("Opportunité liée introuvable — créez une chaîne générique.");
          }
        } catch (e) {
          setPrefillBanner({ opp_titre: "Erreur de chargement" });
        }
      } else if (prefillLevel) {
        setPrefillBanner({ level: prefillLevel });
        notify.success(`Chaîne demandée pour le niveau ${prefillLevel}.`);
      }
      setSearchParams({}, { replace: true });
    })();
  }, [searchParams, setSearchParams]);

  if (activeId) {
    return <ChainEditor id={activeId} onBack={() => { setActiveId(null); load(); }} />;
  }

  return (
    <div className="space-y-6" data-testid="chainage-root">
      <ThreeQPageHeader endpoint="/workspace/chainage/top-priority" />
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[#378ADD] text-[10px] uppercase tracking-[0.2em] font-semibold flex items-center gap-1.5">
            <GitBranch className="w-3 h-3" /> {t("chain.overline", { defaultValue: "Tactique" })}
          </div>
          <h1 className="noam-display text-3xl font-bold mt-1">{t("chain.title", { defaultValue: "Chaînage" })}</h1>
          <p className="text-zinc-400 text-sm mt-1 max-w-2xl">
            {t("chain.subtitle", { defaultValue: "Remontez les 'pourquoi' vers le haut, descendez les 'comment' vers le bas. L'IA identifie le levier le plus puissant." })}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <ModuleHelpButton moduleKey="chainage" />
          <button onClick={() => setCreating(true)} className="btn-primary" data-testid="chain-new-btn">
            <Plus className="w-3.5 h-3.5" /> {t("chain.new", { defaultValue: "Nouvelle chaîne" })}
          </button>
        </div>
      </div>

      <InlineGuide
        id="chainage-intro"
        title="Ici, vous remontez les causes et identifiez les leviers"
        bullets={[
          "Décomposer un objectif ou un problème en chaîne « pourquoi » (vers le haut) et « comment » (vers le bas).",
          "Tagger chaque nœud comme Cause / Intention / Dépendance / Action / Ressource / Mesure pour clarifier sa nature.",
          "Demander à Noam d'identifier le levier d'action le plus puissant pour transformer la chaîne.",
        ]}
        hint="Astuce : commencez par un problème concret du quotidien (ex : « pourquoi ce dossier traîne ? »)."
      />

      {/* V22.5 — Bandeau prefill Friction Gap → Chaînage */}
      {prefillBanner && (
        <div
          className="flex items-start gap-3 p-3 rounded-md border border-[#378ADD]/30 bg-[#378ADD]/10 text-zinc-200 text-xs"
          data-testid="chain-prefill-banner"
        >
          <LinkIcon className="w-4 h-4 mt-0.5 text-[#378ADD] shrink-0" />
          <div className="flex-1 min-w-0">
            {prefillBanner.opp_titre ? (
              <>
                <div className="font-medium text-white">
                  {`Chaîne liée à l'opportunité « ${prefillBanner.opp_titre} »`}
                </div>
                <div className="text-zinc-400 text-[11px] mt-0.5">
                  {prefillBanner.opp_secteur && <>Secteur : <span className="text-zinc-300">{prefillBanner.opp_secteur}</span> · </>}
                  La modal « Nouvelle chaîne » a été préremplie. Cliquez « Créer » pour démarrer.
                </div>
              </>
            ) : (
              <div className="font-medium text-white">
                Chaîne demandée pour le niveau {prefillBanner.level}
              </div>
            )}
          </div>
          <button
            onClick={() => setPrefillBanner(null)}
            className="p-1 rounded hover:bg-white/5 text-zinc-500"
            data-testid="chain-prefill-banner-close"
            title="Fermer"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      {loading ? (
        <div className="text-zinc-400 text-sm flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> {t("common.loading", { defaultValue: "Chargement…" })}</div>
      ) : items.length === 0 ? (
        <div className="noam-card p-8 text-center">
          <GitBranch className="w-8 h-8 text-[#378ADD] mx-auto opacity-60" />
          <div className="noam-display text-lg mt-3">{t("chain.empty_title", { defaultValue: "Aucune chaîne" })}</div>
          <p className="text-zinc-400 text-sm mt-1 max-w-lg mx-auto">
            NOAM peut analyser votre <strong className="text-zinc-200">positionnement</strong> et votre{" "}
            <strong className="text-zinc-200">annuaire</strong> pour générer automatiquement la chaîne causale
            la plus stratégique de votre organisation — pourquoi (causes en amont) et comment (actions en aval).
          </p>
          <div className="flex items-center justify-center gap-3 mt-5 flex-wrap">
            <button
              onClick={async () => {
                setAutoGenBusy(true);
                try {
                  const r = await api.post("/workspace/chains/auto-generate");
                  notify.success(r.data.message || "Chaîne générée.");
                  await load();
                  if (r.data.chain_id) setActiveId(r.data.chain_id);
                } catch (e) {
                  notify.error(fmtError(e, "Génération impossible."));
                } finally {
                  setAutoGenBusy(false);
                }
              }}
              disabled={autoGenBusy}
              className="btn-primary inline-flex items-center gap-2 disabled:opacity-50"
              data-testid="chain-auto-generate"
            >
              {autoGenBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              {autoGenBusy ? "Génération en cours (15-30s)…" : "Générer ma chaîne via NOAM"}
            </button>
            <button
              onClick={() => setCreating(true)}
              disabled={autoGenBusy}
              className="text-xs px-3 py-2 rounded border border-white/10 text-zinc-400 hover:text-zinc-100 inline-flex items-center gap-1.5 disabled:opacity-50"
              data-testid="chain-empty-cta"
            >
              <Plus className="w-3 h-3" /> Démarrer à la main
            </button>
          </div>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((c) => (
            <button key={c.id} onClick={() => setActiveId(c.id)}
              className="noam-card p-5 text-left hover:border-[#378ADD]/50 transition-colors"
              data-testid={`chain-card-${c.id}`}>
              <div className="noam-display text-base font-semibold truncate">{c.title}</div>
              <p className="text-zinc-500 text-xs mt-1.5 line-clamp-2">{c.description || "—"}</p>
              <div className="flex items-center gap-3 mt-3 text-[11px] text-zinc-500">
                <span>{c.node_count} {t("chain.nodes_short", { defaultValue: "nœuds" })}</span>
                <span>·</span>
                <span>{c.link_count} {t("chain.links_short", { defaultValue: "liens" })}</span>
              </div>
            </button>
          ))}
        </div>
      )}

      {creating && (
        <NewChainModal
          prefill={prefillData}
          onClose={() => { setCreating(false); setPrefillData(null); }}
          onCreated={(id) => { setCreating(false); setPrefillData(null); setActiveId(id); }}
        />
      )}
    </div>
  );
}

function NewChainModal({ prefill, onClose, onCreated }) {
  const { t } = useTranslation();
  const [title, setTitle] = useState(prefill?.title || "");
  const [description, setDescription] = useState(prefill?.description || "");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try { const r = await api.post("/workspace/chains", { title, description }); onCreated(r.data.id); }
    catch (e) { notify.error(fmtError(e)); setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose} data-testid="chain-new-modal">
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="noam-card p-6 w-full max-w-md space-y-3">
        <div className="flex items-center justify-between">
          <div className="noam-display text-lg font-semibold">{t("chain.new", { defaultValue: "Nouvelle chaîne" })}</div>
          <button type="button" onClick={onClose} className="text-zinc-400 hover:text-white"><X className="w-4 h-4" /></button>
        </div>
        <input value={title} onChange={(e) => setTitle(e.target.value)} required minLength={2}
          placeholder={t("chain.field_title", { defaultValue: "Titre" })}
          className="noam-input" data-testid="chain-modal-title" />
        <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3}
          placeholder={t("chain.field_desc", { defaultValue: "Description (facultatif)" })}
          className="noam-input resize-none" data-testid="chain-modal-desc" />
        <button type="submit" disabled={busy || !title.trim()} className="btn-primary w-full justify-center disabled:opacity-50" data-testid="chain-modal-submit">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
          {t("common.create", { defaultValue: "Créer" })}
        </button>
      </form>
    </div>
  );
}

function ChainEditor({ id, onBack }) {
  const { t } = useTranslation();
  const [chain, setChain] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [aiBusy, setAiBusy] = useState(false);
  const [valBusy, setValBusy] = useState(false);
  const [adding, setAdding] = useState(null); // {direction} | null
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  // V22.6 — sélection courante pour bouton "Supprimer sélection"
  const [selectedNodeIds, setSelectedNodeIds] = useState([]);
  const [selectedEdgeIds, setSelectedEdgeIds] = useState([]);
  // V23.8 — Tab toggle (graphe | tableau), focal node id, market data, busy set
  const [viewMode, setViewMode] = useState("graph"); // "graph" | "table"
  const [focalNodeId, setFocalNodeId] = useState(null);
  const [rawNodes, setRawNodes] = useState([]); // DB nodes brut
  const [rawLinks, setRawLinks] = useState([]); // DB links brut
  const [nodeMarkets, setNodeMarkets] = useState({}); // {node_id: marketData}
  const [marketBusy, setMarketBusy] = useState(new Set()); // nodes en cours de calcul
  // V23.9 — Bulk compute + sort by value
  const [bulkJob, setBulkJob] = useState(null); // {bulk_job_id, status, computed, total, current, skipped_cache, failed}
  const [sortByValue, setSortByValue] = useState(false); // table view sort toggle

  // V23.14.9 — Heatmap value : max capture annuel parmi les nœuds (pour légende)
  const maxCaptureValue = useMemo(() => {
    let mx = 0;
    for (const m of Object.values(nodeMarkets || {})) {
      if (m && m.is_market_relevant && typeof m.capture_value_meur_annual === "number" && m.capture_value_meur_annual > mx) {
        mx = m.capture_value_meur_annual;
      }
    }
    return mx;
  }, [nodeMarkets]);

  const renameNode = useCallback(async (nodeId, title) => {
    try { await api.patch(`/workspace/chains/${id}/nodes/${nodeId}`, { title }); }
    catch (e) { notify.error(fmtError(e)); }
  }, [id]);

  // V23.8 — Charge tous les market-size connus pour la chaîne
  const loadMarkets = useCallback(async () => {
    try {
      const r = await api.get(`/workspace/chains/${id}/market-size/bulk`);
      setNodeMarkets(r.data?.by_node || {});
    } catch { /* ignore */ }
  }, [id]);

  // V23.8 — Calcule le market-size d'un nœud (async polling)
  const computeNodeMarket = useCallback(async (nodeId, force = false) => {
    setMarketBusy((prev) => new Set(prev).add(nodeId));
    try {
      const startRes = await api.post(
        `/workspace/chains/${id}/nodes/${nodeId}/market-size${force ? "?force_refresh=true" : ""}`
      );
      if (startRes.data?.from_cache) {
        await loadMarkets();
        return;
      }
      const jobId = startRes.data?.job_id;
      if (!jobId) throw new Error("no job_id");
      for (let i = 0; i < 30; i++) {
        await new Promise((r) => setTimeout(r, 2000));
        const poll = await api.get(`/workspace/chains/market-size/jobs/${jobId}`);
        if (poll.data?.status === "done") {
          await loadMarkets();
          notify.success(t("chain.market_done", { defaultValue: "Taille de marché estimée." }));
          return;
        }
        if (poll.data?.status === "failed") throw new Error(poll.data?.error || "job failed");
      }
      throw new Error("timeout");
    } catch (e) {
      notify.error(fmtError(e, t("chain.market_fail", { defaultValue: "Estimation échouée" })));
    } finally {
      setMarketBusy((prev) => {
        const n = new Set(prev); n.delete(nodeId); return n;
      });
    }
  }, [id, loadMarkets, t]);

  // V23.9 — Bulk: calcule tous les nœuds avec barre de progression globale
  const bulkComputeMarkets = useCallback(async (forceRefresh = false) => {
    try {
      const r = await api.post(`/workspace/chains/${id}/market-size/bulk-compute${forceRefresh ? "?force_refresh=true" : ""}`);
      const jobId = r.data?.bulk_job_id;
      if (!jobId) { notify.error("Aucun job retourné"); return; }
      setBulkJob({ bulk_job_id: jobId, status: "queued", computed: 0, total: r.data?.total || 0, skipped_cache: 0, failed: 0 });
      for (let i = 0; i < 240; i++) { // max 8min
        await new Promise((res) => setTimeout(res, 2000));
        try {
          const poll = await api.get(`/workspace/chains/market-size/bulk-jobs/${jobId}`);
          setBulkJob(poll.data);
          if (poll.data?.status === "done" || poll.data?.status === "failed") {
            await loadMarkets();
            if (poll.data?.status === "done") {
              notify.success(t("chain.bulk_done", {
                defaultValue: "Toutes les tailles de marché calculées ({{computed}}/{{total}}, {{cache}} depuis le cache).",
                computed: poll.data?.computed, total: poll.data?.total, cache: poll.data?.skipped_cache,
              }));
            } else {
              notify.error(t("chain.bulk_failed", { defaultValue: "Calcul global échoué" }));
            }
            // Auto-hide progress bar 4s après
            setTimeout(() => setBulkJob(null), 4000);
            return;
          }
        } catch { /* keep polling */ }
      }
      notify.error("Timeout du calcul global");
    } catch (e) {
      notify.error(fmtError(e, "Calcul global échoué"));
      setBulkJob(null);
    }
  }, [id, loadMarkets, t]);

  // eslint-disable-next-line react-hooks/exhaustive-deps -- deps volontairement limitées (recalcul contrôlé)
  const refresh = useCallback(async () => {
    try {
      const r = await api.get(`/workspace/chains/${id}`);
      setChain(r.data.chain);
      setMetrics(r.data.metrics);
      const dbNodes = r.data.nodes || [];
      const dbLinks = r.data.links || [];
      setRawNodes(dbNodes);
      setRawLinks(dbLinks);
      const validation = r.data.chain?.dependency_validation || null;
      const linkCat = {};
      for (const f of (validation?.links || [])) linkCat[f.link_id] = f.category;
      // Focal node : sélection courante OU nœud le + connecté (degré max)
      const degree = {};
      for (const ln of dbLinks) {
        degree[ln.from_node] = (degree[ln.from_node] || 0) + 1;
        degree[ln.to_node] = (degree[ln.to_node] || 0) + 1;
      }
      let focal = focalNodeId;
      if (!focal || !dbNodes.find((n) => n.id === focal)) {
        focal = dbNodes.length > 0 ? dbNodes.reduce((a, b) => (degree[a.id] || 0) >= (degree[b.id] || 0) ? a : b).id : null;
      }
      // V23.14.9 — Heatmap value : max capture value annuel pour normaliser
      let maxCapture = 0;
      for (const n of dbNodes) {
        const m = nodeMarkets[n.id];
        if (m && m.is_market_relevant && typeof m.capture_value_meur_annual === "number" && m.capture_value_meur_annual > maxCapture) {
          maxCapture = m.capture_value_meur_annual;
        }
      }
      // Transform DB nodes → ReactFlow nodes (custom 'editable' type)
      const flowNodes = dbNodes.map((n) => {
        const m = nodeMarkets[n.id] || null;
        // Intensité chauffe 0..1 — racine carrée pour adoucir le scaling (queue de Gauss)
        let heatIntensity = 0;
        if (m && m.is_market_relevant && typeof m.capture_value_meur_annual === "number" && maxCapture > 0) {
          heatIntensity = Math.sqrt(Math.max(0, m.capture_value_meur_annual) / maxCapture);
        }
        return {
          id: n.id,
          type: "editable",
          position: { x: n.x ?? Math.random() * 600, y: n.y ?? (n.direction === "up" ? -180 * n.level : 180 * n.level) },
          data: {
            label: n.title,
            type: n.type,
            direction: n.direction,
            level: n.level,
            isFocal: n.id === focal,
            market: m,
            heatIntensity,
            maxCapture,
            onRename: renameNode,
          },
        };
      });
      // V23.8 — Edge colors : strength prioritaire, validation override si présent
      const VAL_COLOR = { confirmed: "#16a34a", plausible: "#d97706", phantom: "#dc2626" };
      const flowEdges = dbLinks.map((ln) => {
        const cat = linkCat[ln.id];
        const color = cat ? VAL_COLOR[cat] : edgeColorByStrength(ln.strength || 5);
        return {
          id: ln.id,
          source: ln.from_node,
          target: ln.to_node,
          animated: cat === "phantom" ? false : (ln.link_type === "cause_effet"),
          label: cat ? `${cat.charAt(0).toUpperCase()}${cat.slice(1, 4)} · ${ln.strength}/10` : `${ln.strength}/10`,
          labelStyle: { fill: color, fontWeight: 700, fontSize: 11 },
          labelBgStyle: { fill: "#0A1A2F", fillOpacity: 0.8 },
          markerEnd: { type: MarkerType.ArrowClosed, color },
          style: {
            stroke: color,
            strokeWidth: Math.max(1.5, (ln.strength || 5) / 2.5),
            strokeDasharray: cat === "phantom" ? "4 4" : undefined,
          },
        };
      });
      // Suggested missing dependencies — dashed blue ghost edges
      for (const m of (validation?.missing || [])) {
        flowEdges.push({
          id: `suggested-${m.from_node}-${m.to_node}`,
          source: m.from_node,
          target: m.to_node,
          label: t("chain.suggested", { defaultValue: "Suggéré" }),
          labelStyle: { fill: "#60a5fa", fontWeight: 600, fontSize: 10 },
          labelBgStyle: { fill: "#0A1A2F", fillOpacity: 0.7 },
          markerEnd: { type: MarkerType.ArrowClosed, color: "#60a5fa" },
          style: { stroke: "#60a5fa", strokeWidth: 1.5, strokeDasharray: "6 4" },
        });
      }
      setNodes(flowNodes);
      setEdges(flowEdges);
      if (focal && focal !== focalNodeId) setFocalNodeId(focal);
    } catch (e) { notify.error(fmtError(e)); }
  }, [id, setNodes, setEdges, renameNode, t, focalNodeId, nodeMarkets]);

  useEffect(() => { refresh(); }, [refresh]);

  // Charge les markets une fois au mount + à chaque changement de chaîne
  useEffect(() => { loadMarkets(); }, [loadMarkets]);

  // Persist node positions after drag
  const onNodeDragStop = useCallback(async (_evt, node) => {
    if (!node?.position) return;
    try {
      await api.patch(`/workspace/chains/${id}/nodes/${node.id}`, {
        x: Math.round(node.position.x), y: Math.round(node.position.y),
      });
    } catch (e) {
      notify.error(fmtError(e));
    }
  }, [id]);

  const onConnect = useCallback(async (params) => {
    try {
      await api.post(`/workspace/chains/${id}/links`, { from_node: params.source, to_node: params.target, link_type: "cause_effet", strength: 5 });
      refresh();
    } catch (e) { notify.error(fmtError(e)); }
  }, [id, refresh]);

  const importUtility = async () => {
    // V22.5 — Demande à l'utilisateur combien d'acteurs importer (défaut 12, max 30)
    // pour éviter l'inondation du graphe (avant : import des 46+ acteurs de l'org).
    const raw = window.prompt(
      t("chain.import_top_prompt", {
        defaultValue: "Combien d'acteurs Utility Mapping importer ? (max 30, par défaut 12 — triés par criticité, dédupliqués)",
      }),
      "12",
    );
    if (raw === null) return; // user cancelled
    const top = Math.max(1, Math.min(30, parseInt(raw, 10) || 12));
    try {
      const r = await api.post(`/workspace/chains/${id}/import/utility-actors?top=${top}`);
      const d = r.data || {};
      notify.success(t("chain.import_done_v2", {
        defaultValue: "{{n}} acteur(s) importé(s) (sur {{avail}} disponibles, triés par criticité).",
        n: d.created || 0,
        avail: d.total_available || 0,
      }));
      refresh();
    } catch (e) { notify.error(fmtError(e)); }
  };

  // V22.6 — Suppression de la sélection courante (nœuds + liens) et clear complet
  const onSelectionChange = useCallback(({ nodes: selN = [], edges: selE = [] }) => {
    setSelectedNodeIds(selN.map((n) => n.id));
    setSelectedEdgeIds(selE.map((e) => e.id));
  }, []);

  const deleteSelection = async () => {
    const nodeCount = selectedNodeIds.length;
    const edgeCount = selectedEdgeIds.length;
    if (nodeCount === 0 && edgeCount === 0) {
      notify.error(t("chain.no_selection", { defaultValue: "Aucune sélection. Cliquez sur un nœud ou un lien d'abord." }));
      return;
    }
    const msg = t("chain.confirm_delete_selection", {
      defaultValue: "Supprimer {{n}} nœud(s) et {{l}} lien(s) sélectionné(s) ? Les liens connectés aux nœuds supprimés disparaîtront aussi.",
      n: nodeCount, l: edgeCount,
    });
    if (!window.confirm(msg)) return;
    try {
      // Supprimer d'abord les edges sélectionnés isolés
      for (const eid of selectedEdgeIds) {
        await api.delete(`/workspace/chains/${id}/links/${eid}`).catch(() => {});
      }
      // Puis les nœuds (le backend cascade les liens reliés)
      for (const nid of selectedNodeIds) {
        await api.delete(`/workspace/chains/${id}/nodes/${nid}`).catch(() => {});
      }
      setSelectedNodeIds([]); setSelectedEdgeIds([]);
      notify.success(t("chain.delete_selection_done", {
        defaultValue: "{{n}} nœud(s) et {{l}} lien(s) supprimés.",
        n: nodeCount, l: edgeCount,
      }));
      refresh();
    } catch (e) { notify.error(fmtError(e)); }
  };

  const clearAll = async () => {
    const msg = t("chain.confirm_clear_all", {
      defaultValue: "Vider TOUT le graphe (nœuds + liens + lecture IA) ? La chaîne et son titre sont conservés. Action irréversible.",
    });
    if (!window.confirm(msg)) return;
    try {
      const r = await api.post(`/workspace/chains/${id}/clear`);
      const d = r.data || {};
      setSelectedNodeIds([]); setSelectedEdgeIds([]);
      notify.success(t("chain.clear_done", {
        defaultValue: "Graphe vidé : {{n}} nœud(s) et {{l}} lien(s) supprimés.",
        n: d.nodes_deleted || 0, l: d.links_deleted || 0,
      }));
      refresh();
    } catch (e) { notify.error(fmtError(e)); }
  };

  const runAI = async () => {
    setAiBusy(true);
    try { const r = await api.post(`/workspace/chains/${id}/ai-lever`); setChain((c) => ({ ...c, ai_reading: r.data.ai_reading })); setMetrics(r.data.metrics); notify.success(t("chain.ai_done", { defaultValue: "Levier identifié." })); }
    catch (e) { notify.error(fmtError(e)); }
    setAiBusy(false);
  };

  const validateDeps = async () => {
    setValBusy(true);
    try {
      await api.post(`/workspace/chains/${id}/validate-dependencies`);
      notify.success(t("chain.validate_done", { defaultValue: "Validation IA prête." }));
      refresh();
    } catch (e) { notify.error(fmtError(e)); }
    setValBusy(false);
  };

  const acceptSuggestion = async (from, to) => {
    try {
      await api.post(`/workspace/chains/${id}/dependency-suggestions/${from}/${to}/accept`);
      notify.success(t("chain.suggestion_accepted", { defaultValue: "Lien créé." }));
      refresh();
    } catch (e) { notify.error(fmtError(e)); }
  };
  const rejectSuggestion = async (from, to) => {
    try {
      await api.post(`/workspace/chains/${id}/dependency-suggestions/${from}/${to}/reject`);
      refresh();
    } catch (e) { notify.error(fmtError(e)); }
  };

  const downloadValidationPdf = async () => {
    try {
      const r = await api.get(`/workspace/chains/${id}/dependency-validation/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `chaine-validation-${id.slice(0, 8)}.pdf`;
      document.body.appendChild(a); a.click();
      document.body.removeChild(a); window.URL.revokeObjectURL(url);
    } catch (e) { notify.error(fmtError(e)); }
  };

  const downloadPdf = async () => {
    try {
      const r = await api.get(`/workspace/chains/${id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `chaine-${id.slice(0, 8)}.pdf`;
      document.body.appendChild(a); a.click();
      document.body.removeChild(a); window.URL.revokeObjectURL(url);
    } catch (e) { notify.error(fmtError(e)); }
  };

  if (!chain) {
    return <div className="text-zinc-400 text-sm flex items-center gap-2" data-testid="chain-editor-loading"><Loader2 className="w-4 h-4 animate-spin" /> {t("common.loading", { defaultValue: "Chargement…" })}</div>;
  }

  return (
    <div className="space-y-4" data-testid="chain-editor-root">
      <button onClick={onBack} className="inline-flex items-center gap-1.5 text-xs text-zinc-400 hover:text-white" data-testid="chain-editor-back">
        <ChevronLeft className="w-3.5 h-3.5" /> {t("common.back", { defaultValue: "Retour" })}
      </button>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1">
          <h1 className="noam-display text-2xl font-bold">{chain.title}</h1>
          <p className="text-zinc-400 text-sm mt-1">{chain.description || "—"}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button onClick={() => setAdding({ direction: "up" })} className="btn-secondary text-xs" data-testid="chain-add-up">
            <ArrowUpToLine className="w-3 h-3" /> {t("chain.add_why", { defaultValue: "Pourquoi ↑" })}
          </button>
          <button onClick={() => setAdding({ direction: "down" })} className="btn-secondary text-xs" data-testid="chain-add-down">
            <ArrowDownToLine className="w-3 h-3" /> {t("chain.add_how", { defaultValue: "Comment ↓" })}
          </button>
          <button onClick={importUtility} className="btn-secondary text-xs" data-testid="chain-import-utility">
            <LinkIcon className="w-3 h-3" /> {t("chain.import_utility", { defaultValue: "Importer Utility Mapping" })}
          </button>
          {/* V22.6 — Suppression sélection + Clear complet */}
          <button
            onClick={deleteSelection}
            disabled={selectedNodeIds.length === 0 && selectedEdgeIds.length === 0}
            className="btn-secondary text-xs disabled:opacity-40"
            data-testid="chain-delete-selection"
            title={t("chain.delete_selection_title", { defaultValue: "Cliquer un nœud / lien dans le graphe puis supprimer" })}
          >
            <Trash2 className="w-3 h-3" />
            {t("chain.delete_selection", { defaultValue: "Supprimer sélection" })}
            {(selectedNodeIds.length + selectedEdgeIds.length) > 0 && (
              <span className="ml-1 text-[10px] px-1 rounded bg-[#E24B4A]/20 text-[#E24B4A]">
                {selectedNodeIds.length + selectedEdgeIds.length}
              </span>
            )}
          </button>
          <button
            onClick={clearAll}
            className="btn-secondary text-xs border-[#E24B4A]/30 text-[#FCA5A5] hover:border-[#E24B4A]/50"
            data-testid="chain-clear-all"
            title={t("chain.clear_all_title", { defaultValue: "Vide TOUT le graphe (nœuds + liens + lecture IA), garde la chaîne" })}
          >
            <XCircle className="w-3 h-3" /> {t("chain.clear_all", { defaultValue: "Tout nettoyer" })}
          </button>
          <button onClick={validateDeps} disabled={valBusy} className="btn-secondary text-xs disabled:opacity-50" data-testid="chain-validate-btn">
            {valBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <ShieldCheck className="w-3 h-3" />}
            {t("chain.validate_btn", { defaultValue: "Valider les dépendances" })}
          </button>
          <PersonaAction action="export_pdf">
            <button onClick={downloadPdf} className="btn-secondary text-xs" data-testid="chain-pdf-btn">
              <FileText className="w-3 h-3" /> {t("chain.pdf", { defaultValue: "Export PDF" })}
            </button>
          </PersonaAction>
          <button onClick={runAI} disabled={aiBusy} className="btn-primary text-xs disabled:opacity-50" data-testid="chain-ai-btn">
            {aiBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
            {t("chain.ai_btn", { defaultValue: "Levier IA" })}
          </button>
        </div>
      </div>
      <p className="text-[11px] text-zinc-500" data-testid="chain-editor-tip">
        {t("chain.tip", { defaultValue: "Double-clic sur un nœud pour renommer · drag pour déplacer · drag d'un point à l'autre pour créer un lien · clic pour sélectionner puis « Supprimer sélection »." })}
      </p>

      <DocumentAttachZone module="chainage" entityId={chain.id} entityLabel={chain.title} />

      {/* V23.8 — Toggle Vue Graphe / Vue Tableau + V23.9 Bulk compute */}
      <div className="flex items-center gap-1.5 flex-wrap" data-testid="chain-view-toggle">
        <button
          onClick={() => setViewMode("graph")}
          className={`text-xs px-3 py-1.5 rounded-md inline-flex items-center gap-1.5 border transition ${
            viewMode === "graph"
              ? "bg-[#378ADD]/20 border-[#378ADD]/40 text-white"
              : "bg-white/[0.02] border-white/5 text-zinc-500 hover:text-zinc-300"
          }`}
          data-testid="chain-view-graph"
        >
          <Network className="w-3 h-3" />
          {t("chain.view_graph", { defaultValue: "Vue graphe" })}
        </button>
        <button
          onClick={() => setViewMode("table")}
          className={`text-xs px-3 py-1.5 rounded-md inline-flex items-center gap-1.5 border transition ${
            viewMode === "table"
              ? "bg-[#378ADD]/20 border-[#378ADD]/40 text-white"
              : "bg-white/[0.02] border-white/5 text-zinc-500 hover:text-zinc-300"
          }`}
          data-testid="chain-view-table"
        >
          <Rows className="w-3 h-3" />
          {t("chain.view_table", { defaultValue: "Vue tableau (amont · focal · aval)" })}
        </button>

        {viewMode === "table" && (
          <button
            onClick={() => setSortByValue((v) => !v)}
            className={`text-xs px-3 py-1.5 rounded-md inline-flex items-center gap-1.5 border transition ${
              sortByValue
                ? "bg-[#1D9E75]/20 border-[#1D9E75]/40 text-[#1D9E75]"
                : "bg-white/[0.02] border-white/5 text-zinc-500 hover:text-zinc-300"
            }`}
            data-testid="chain-sort-by-value"
            title={t("chain.sort_by_value_tip", { defaultValue: "Trier les nœuds par valeur potentielle annuelle décroissante." })}
          >
            <BarChart3 className="w-3 h-3" />
            {t("chain.sort_by_value", { defaultValue: "Trier par valeur ↓" })}
          </button>
        )}

        <div className="ml-auto inline-flex items-center gap-1.5">
          <button
            onClick={() => bulkComputeMarkets(false)}
            disabled={!!bulkJob && bulkJob.status !== "done" && bulkJob.status !== "failed"}
            className="text-xs px-3 py-1.5 rounded-md inline-flex items-center gap-1.5 border bg-[#EF9F27]/10 border-[#EF9F27]/40 text-[#EF9F27] hover:bg-[#EF9F27]/20 disabled:opacity-50"
            data-testid="chain-bulk-compute-btn"
            title={t("chain.bulk_compute_tip", { defaultValue: "Calcule la taille de marché de tous les nœuds en parallèle (3 à la fois)." })}
          >
            {(bulkJob && bulkJob.status !== "done" && bulkJob.status !== "failed")
              ? <Loader2 className="w-3 h-3 animate-spin" />
              : <Sparkles className="w-3 h-3" />}
            {bulkJob && bulkJob.status !== "done" && bulkJob.status !== "failed"
              ? t("chain.bulk_running", { defaultValue: "Calcul global en cours…" })
              : t("chain.bulk_compute", { defaultValue: "Calculer tout (marchés)" })}
          </button>
        </div>
      </div>

      {/* Barre de progression globale */}
      {bulkJob && (
        <div className="noam-card p-3" data-testid="chain-bulk-progress">
          <div className="flex items-center justify-between gap-2 mb-1.5 text-[11px]">
            <span className="text-zinc-300 font-medium inline-flex items-center gap-1.5">
              <BarChart3 className="w-3 h-3 text-[#EF9F27]" />
              {bulkJob.status === "done"
                ? t("chain.bulk_finished", { defaultValue: "Calcul global terminé" })
                : bulkJob.status === "failed"
                  ? t("chain.bulk_failed_short", { defaultValue: "Calcul échoué" })
                  : t("chain.bulk_in_progress", { defaultValue: "Calcul des marchés en cours…" })}
            </span>
            <span className="text-zinc-500">
              {bulkJob.computed}/{bulkJob.total}
              {bulkJob.skipped_cache > 0 && <span className="text-[#1D9E75] ml-1.5">· cache: {bulkJob.skipped_cache}</span>}
              {bulkJob.failed > 0 && <span className="text-[#E24B4A] ml-1.5">· échec: {bulkJob.failed}</span>}
            </span>
          </div>
          <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
            <div
              className={`h-full transition-all duration-500 ${bulkJob.status === "failed" ? "bg-[#E24B4A]" : "bg-gradient-to-r from-[#EF9F27] to-[#1D9E75]"}`}
              style={{ width: `${bulkJob.total > 0 ? Math.round((bulkJob.computed / bulkJob.total) * 100) : 0}%` }}
            />
          </div>
          {bulkJob.current && bulkJob.status === "running" && (
            <div className="text-[10px] text-zinc-500 mt-1 truncate">{t("chain.bulk_current", { defaultValue: "En cours" })}: {bulkJob.current}</div>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        <div className="lg:col-span-3 noam-card p-0 overflow-hidden relative" style={{ height: viewMode === "table" ? 700 : 560 }} data-testid="chain-graph">
          {viewMode === "graph" ? (
            <ReactFlow
              nodes={nodes}
              edges={edges}
              nodeTypes={nodeTypes}
              onNodesChange={onNodesChange}
              onEdgesChange={onEdgesChange}
              onConnect={onConnect}
              onNodeDragStop={onNodeDragStop}
              onSelectionChange={onSelectionChange}
              onNodeClick={(_e, node) => setFocalNodeId(node.id)}
              fitView
              colorMode="dark"
            >
              <Background gap={16} color="#1f2937" />
              <Controls />
              {/* V23.14.9 — Légende heatmap value */}
              {maxCaptureValue > 0 && (
                <div
                  className="absolute top-2 right-2 z-10 p-2 rounded-md bg-black/70 backdrop-blur-sm border border-white/10 text-[10px] text-zinc-200 shadow-lg"
                  data-testid="chain-heatmap-legend"
                  style={{ pointerEvents: "none" }}
                >
                  <div className="flex items-center gap-1.5 mb-1 font-semibold uppercase tracking-wider text-[9px] text-[#EFD127]">
                    Heatmap valeur · capture/an
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div
                      className="h-2 w-24 rounded"
                      style={{
                        background: "linear-gradient(to right, rgba(239,209,39,0.15), rgba(239,209,39,0.9))",
                      }}
                    />
                    <span className="text-zinc-400 whitespace-nowrap">→ {fmtMeur(maxCaptureValue)}€</span>
                  </div>
                  <div className="text-[9px] text-zinc-500 mt-0.5">
                    Bordure dorée ∝ valeur potentielle annuelle
                  </div>
                </div>
              )}
            </ReactFlow>
          ) : (
            <ChainTableView
              rawNodes={rawNodes}
              rawLinks={rawLinks}
              focalNodeId={focalNodeId}
              setFocalNodeId={setFocalNodeId}
              nodeMarkets={nodeMarkets}
              marketBusy={marketBusy}
              onComputeMarket={computeNodeMarket}
              sortByValue={sortByValue}
              t={t}
            />
          )}
        </div>
        <div className="space-y-3">
          <div className="noam-card p-4">
            <div className="text-[10px] uppercase tracking-wider text-[#378ADD] font-semibold">{t("chain.metrics", { defaultValue: "Métriques" })}</div>
            <div className="grid grid-cols-2 gap-2 mt-2 text-xs">
              <Metric label={t("chain.nodes", { defaultValue: "Nœuds" })} v={metrics?.node_count} />
              <Metric label={t("chain.links", { defaultValue: "Liens" })} v={metrics?.link_count} />
              <Metric label={t("chain.depth_up", { defaultValue: "Profondeur ↑" })} v={metrics?.depth_up} />
              <Metric label={t("chain.depth_down", { defaultValue: "Profondeur ↓" })} v={metrics?.depth_down} />
            </div>
            {metrics?.critical_nodes?.length > 0 && (
              <div className="mt-3">
                <div className="text-[10px] uppercase text-zinc-500">{t("chain.critical", { defaultValue: "Nœuds critiques" })}</div>
                <ul className="mt-1 space-y-1 text-xs">
                  {metrics.critical_nodes.map((n) => (
                    <li key={n.id} className="text-zinc-300">• {n.title} <span className="text-zinc-500">({n.degree} liens)</span></li>
                  ))}
                </ul>
              </div>
            )}
          </div>
          {chain.ai_reading && (
            <div className="noam-card p-4">
              <div className="text-[10px] uppercase tracking-wider text-[#378ADD] font-semibold flex items-center gap-1.5">
                <Sparkles className="w-3 h-3" /> {t("chain.ai_reading", { defaultValue: "Lecture IA" })}
              </div>
              <pre className="whitespace-pre-wrap text-xs text-zinc-200 mt-2 font-sans"><AiText>{chain.ai_reading}</AiText></pre>
            </div>
          )}
          {chain.dependency_validation && (
            <ValidationPanel
              validation={chain.dependency_validation}
              chainNodes={nodes}
              onAccept={acceptSuggestion}
              onReject={rejectSuggestion}
              onPdf={downloadValidationPdf}
              t={t}
            />
          )}
        </div>
      </div>

      {adding && (
        <AddNodeModal
          direction={adding.direction}
          onClose={() => setAdding(null)}
          onCreate={async (title, type) => {
            try {
              await api.post(`/workspace/chains/${id}/nodes`, { title, type, direction: adding.direction, level: 1 });
              setAdding(null);
              refresh();
            } catch (e) { notify.error(fmtError(e)); }
          }}
        />
      )}
    </div>
  );
}

function AddNodeModal({ direction, onClose, onCreate }) {
  const { t } = useTranslation();
  const [title, setTitle] = useState("");
  const [type, setType] = useState(direction === "up" ? "cause" : "action");
  const submit = (e) => { e.preventDefault(); const v = title.trim(); if (v) onCreate(v, type); };
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose} data-testid="chain-add-node-modal">
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="noam-card p-5 w-full max-w-sm space-y-3">
        <div className="flex items-center justify-between">
          <div className="noam-display text-base font-semibold">
            {direction === "up"
              ? t("chain.add_why", { defaultValue: "Pourquoi ↑" })
              : t("chain.add_how", { defaultValue: "Comment ↓" })}
          </div>
          <button type="button" onClick={onClose} className="text-zinc-400 hover:text-white"><X className="w-4 h-4" /></button>
        </div>
        <input value={title} onChange={(e) => setTitle(e.target.value)} required minLength={1} autoFocus
          placeholder={t("chain.node_title_placeholder", { defaultValue: "Titre du nœud" })}
          className="noam-input" data-testid="chain-add-node-title" />
        <select value={type} onChange={(e) => setType(e.target.value)} className="noam-input" data-testid="chain-add-node-type">
          {NODE_TYPES.map((nt) => (
            <option key={nt} value={nt}>{t(`chain.type_${nt}`, { defaultValue: nt })}</option>
          ))}
        </select>
        <button type="submit" disabled={!title.trim()} className="btn-primary w-full justify-center disabled:opacity-50" data-testid="chain-add-node-submit">
          <Check className="w-3.5 h-3.5" /> {t("common.create", { defaultValue: "Créer" })}
        </button>
      </form>
    </div>
  );
}

function Metric({ label, v }) {
  return (
    <div>
      <div className="text-[10px] text-zinc-500">{label}</div>
      <div className="text-base font-semibold text-white">{v ?? "—"}</div>
    </div>
  );
}

function ValidationPanel({ validation, chainNodes, onAccept, onReject, onPdf, t }) {
  const titleOf = (id) => {
    const n = chainNodes.find((nn) => nn.id === id);
    return n?.data?.label || id.slice(0, 8);
  };
  const links = validation.links || [];
  const missing = validation.missing || [];
  const counts = { confirmed: 0, plausible: 0, phantom: 0 };
  for (const f of links) counts[f.category] = (counts[f.category] || 0) + 1;
  const CAT_BG = { confirmed: "#1D9E75", plausible: "#EF9F27", phantom: "#E24B4A" };
  const CAT_ICON = {
    confirmed: <ShieldCheck className="w-3 h-3" />,
    plausible: <AlertTriangle className="w-3 h-3" />,
    phantom: <XCircle className="w-3 h-3" />,
  };
  return (
    <div className="noam-card p-4" data-testid="chain-validation-panel">
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-wider text-[#378ADD] font-semibold flex items-center gap-1.5">
          <ShieldCheck className="w-3 h-3" /> {t("chain.validation_title", { defaultValue: "Validation épistémologique" })}
        </div>
        <button onClick={onPdf} className="text-[10px] text-[#378ADD] hover:underline inline-flex items-center gap-1" data-testid="chain-validation-pdf-btn">
          <FileText className="w-3 h-3" /> PDF
        </button>
      </div>
      <div className="grid grid-cols-4 gap-1.5 mt-2 text-[10px]">
        {["confirmed", "plausible", "phantom"].map((k) => (
          <div key={k} className="rounded p-1.5 text-center"
            style={{ background: `${CAT_BG[k]}22`, border: `1px solid ${CAT_BG[k]}55`, color: CAT_BG[k] }}>
            <div className="font-bold text-sm">{counts[k] || 0}</div>
            <div className="uppercase tracking-wider">{t(`chain.cat_${k}`, { defaultValue: k })}</div>
          </div>
        ))}
        <div className="rounded p-1.5 text-center bg-[#60a5fa22] border border-[#60a5fa55] text-[#60a5fa]">
          <div className="font-bold text-sm">{missing.length}</div>
          <div className="uppercase tracking-wider">{t("chain.cat_missing", { defaultValue: "manquants" })}</div>
        </div>
      </div>
      {validation.narrative && (
        <div className="mt-3 text-xs text-zinc-200 leading-relaxed border-l-2 border-[#378ADD]/40 pl-2.5" data-testid="chain-validation-narrative">
          {validation.narrative}
        </div>
      )}
      {links.length > 0 && (
        <div className="mt-3">
          <div className="text-[10px] uppercase text-zinc-500 mb-1">{t("chain.validation_links", { defaultValue: "Liens analysés" })}</div>
          <ul className="space-y-1 max-h-48 overflow-y-auto pr-1">
            {links.map((f) => (
              <li key={f.link_id} className="text-[11px] text-zinc-300 flex items-start gap-1.5" data-testid={`chain-validation-link-${f.link_id}`}>
                <span className="inline-flex items-center gap-0.5 px-1 py-0.5 rounded text-[9px] uppercase font-bold"
                  style={{ background: `${CAT_BG[f.category]}22`, color: CAT_BG[f.category] }}>
                  {CAT_ICON[f.category]} {(f.category || "").slice(0, 3)}
                </span>
                <span className="flex-1"><span className="text-zinc-500">·</span> {f.justification || "—"}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {missing.length > 0 && (
        <div className="mt-3">
          <div className="text-[10px] uppercase text-zinc-500 mb-1">{t("chain.validation_missing", { defaultValue: "Liens manquants suggérés" })}</div>
          <ul className="space-y-1.5">
            {missing.map((m) => (
              <li key={`${m.from_node}-${m.to_node}`} className="text-[11px] text-zinc-300 rounded p-2 bg-[#60a5fa14] border border-[#60a5fa33]" data-testid={`chain-validation-missing-${m.from_node}-${m.to_node}`}>
                <div className="font-semibold text-[#60a5fa]">{titleOf(m.from_node)} ⇢ {titleOf(m.to_node)}</div>
                <div className="text-zinc-400 mt-0.5">{m.rationale}</div>
                <div className="flex gap-1.5 mt-2">
                  <button onClick={() => onAccept(m.from_node, m.to_node)}
                    className="text-[10px] px-2 py-0.5 rounded bg-[#1D9E75]/30 border border-[#1D9E75]/50 text-[#86efac] hover:bg-[#1D9E75]/50"
                    data-testid={`chain-validation-accept-${m.from_node}-${m.to_node}`}>
                    <Check className="w-2.5 h-2.5 inline mr-0.5" /> {t("chain.accept", { defaultValue: "Accepter" })}
                  </button>
                  <button onClick={() => onReject(m.from_node, m.to_node)}
                    className="text-[10px] px-2 py-0.5 rounded border border-white/10 text-zinc-400 hover:bg-white/5"
                    data-testid={`chain-validation-reject-${m.from_node}-${m.to_node}`}>
                    <X className="w-2.5 h-2.5 inline mr-0.5" /> {t("chain.reject", { defaultValue: "Rejeter" })}
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}


// ============================================================================
// V23.8 — ChainTableView : vue tableau 3 colonnes inspirée Cartographie des dépendances.
// Colonne gauche : amont (les "pourquoi"). Colonne centrale : nœud focal. Colonne droite : aval.
// Scroll vertical fluide. Clic sur un nœud → devient le nouveau focal.
// ============================================================================
function ChainTableView({ rawNodes, rawLinks, focalNodeId, setFocalNodeId, nodeMarkets, marketBusy, onComputeMarket, sortByValue, t }) {
  const byId = useMemo(() => {
    const m = {}; for (const n of rawNodes) m[n.id] = n; return m;
  }, [rawNodes]);

  const { upstreamMap, downstreamMap } = useMemo(() => {
    const up = {}; const dn = {};
    for (const l of rawLinks) {
      (up[l.to_node] = up[l.to_node] || []).push({ id: l.from_node, link: l });
      (dn[l.from_node] = dn[l.from_node] || []).push({ id: l.to_node, link: l });
    }
    return { upstreamMap: up, downstreamMap: dn };
  }, [rawLinks]);

  const focal = focalNodeId ? byId[focalNodeId] : null;

  // Helper : récupère valeur de capture annuelle pour le tri
  const valueOf = (nodeId) => {
    const m = nodeMarkets[nodeId];
    return (m && m.is_market_relevant) ? (m.capture_value_meur_annual ?? -1) : -1;
  };

  const upstream = useMemo(() => {
    if (!focal) return [];
    const out = []; const seen = new Set([focal.id]);
    const queue = [{ id: focal.id, depth: 0 }];
    while (queue.length) {
      const cur = queue.shift();
      const parents = upstreamMap[cur.id] || [];
      for (const p of parents) {
        if (seen.has(p.id)) continue;
        seen.add(p.id);
        out.push({ node: byId[p.id], depth: cur.depth + 1, link: p.link });
        queue.push({ id: p.id, depth: cur.depth + 1 });
      }
    }
    if (sortByValue) {
      return out.sort((a, b) => valueOf(b.node.id) - valueOf(a.node.id) || a.depth - b.depth);
    }
    return out.sort((a, b) => a.depth - b.depth || (a.node?.level || 0) - (b.node?.level || 0));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focal, upstreamMap, byId, sortByValue, nodeMarkets]);

  const downstream = useMemo(() => {
    if (!focal) return [];
    const out = []; const seen = new Set([focal.id]);
    const queue = [{ id: focal.id, depth: 0 }];
    while (queue.length) {
      const cur = queue.shift();
      const children = downstreamMap[cur.id] || [];
      for (const c of children) {
        if (seen.has(c.id)) continue;
        seen.add(c.id);
        out.push({ node: byId[c.id], depth: cur.depth + 1, link: c.link });
        queue.push({ id: c.id, depth: cur.depth + 1 });
      }
    }
    if (sortByValue) {
      return out.sort((a, b) => valueOf(b.node.id) - valueOf(a.node.id) || a.depth - b.depth);
    }
    return out.sort((a, b) => a.depth - b.depth || (a.node?.level || 0) - (b.node?.level || 0));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focal, downstreamMap, byId, sortByValue, nodeMarkets]);

  if (rawNodes.length === 0) {
    return (
      <div className="h-full flex items-center justify-center text-zinc-500 text-sm" data-testid="chain-table-empty">
        {t("chain.table_empty", { defaultValue: "Ajoutez des nœuds pour explorer la chaîne en vue tableau." })}
      </div>
    );
  }

  if (!focal) {
    return (
      <div className="h-full p-6 overflow-y-auto" data-testid="chain-table-select-focal">
        <div className="text-xs text-zinc-400 mb-3">
          {t("chain.table_pick_focal", { defaultValue: "Choisissez un nœud focal pour explorer ses amonts (les pourquoi) et ses avals (les comment) :" })}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {rawNodes.map((n) => (
            <button key={n.id} onClick={() => setFocalNodeId(n.id)}
              className="text-left p-3 rounded-lg border border-white/10 bg-white/[0.02] hover:border-[#378ADD]/40 transition"
              data-testid={`chain-table-pick-${n.id}`}>
              <div className="text-[10px] uppercase tracking-wider opacity-70" style={{ color: TYPE_COLORS[n.type] || "#378ADD" }}>
                {TYPE_LABELS_FR[n.type] || n.type} · N{n.level} · {n.direction === "up" ? "↑" : "↓"}
              </div>
              <div className="text-sm text-white font-medium mt-0.5">{n.title}</div>
            </button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-[1fr_minmax(280px,1.2fr)_1fr] gap-0 h-full" data-testid="chain-table-root">
      {/* Colonne AMONT */}
      <div className="border-r border-white/5 flex flex-col min-h-0">
        <div className="px-3 py-2.5 border-b border-white/5 bg-white/[0.02] flex items-center gap-1.5">
          <ArrowUpToLine className="w-3 h-3 text-[#7F77DD]" />
          <span className="text-[10px] uppercase tracking-wider text-[#7F77DD] font-bold">
            {t("chain.table_upstream", { defaultValue: "Amont · Les pourquoi" })}
          </span>
          <span className="ml-auto text-[10px] text-zinc-500">{upstream.length}</span>
        </div>
        <div className="flex-1 overflow-y-auto p-2 space-y-1.5" data-testid="chain-table-upstream">
          {upstream.length === 0 ? (
            <div className="text-[11px] text-zinc-600 px-2 py-3 text-center italic">
              {t("chain.table_no_upstream", { defaultValue: "Aucun amont — ce nœud est une racine." })}
            </div>
          ) : (
            upstream.map((u, i) => (
              <TableNodeCard
                key={`up-${u.node.id}-${i}`}
                node={u.node}
                depth={u.depth}
                link={u.link}
                onClick={() => setFocalNodeId(u.node.id)}
                market={nodeMarkets[u.node.id]}
                busy={marketBusy.has(u.node.id)}
                onComputeMarket={() => onComputeMarket(u.node.id)}
                t={t}
              />
            ))
          )}
        </div>
      </div>

      {/* Colonne FOCAL */}
      <div className="border-r border-white/5 flex flex-col min-h-0 bg-gradient-to-b from-[#378ADD]/5 to-transparent">
        <div className="px-3 py-2.5 border-b border-white/5 bg-[#378ADD]/10 flex items-center gap-1.5">
          <Target className="w-3 h-3 text-[#378ADD]" />
          <span className="text-[10px] uppercase tracking-wider text-[#378ADD] font-bold">
            {t("chain.table_focal", { defaultValue: "Nœud focal" })}
          </span>
        </div>
        <div className="flex-1 overflow-y-auto p-3 flex items-start justify-center">
          <FocalNodeCard
            node={focal}
            market={nodeMarkets[focal.id]}
            busy={marketBusy.has(focal.id)}
            onComputeMarket={() => onComputeMarket(focal.id)}
            onForceRefresh={() => onComputeMarket(focal.id, true)}
            t={t}
          />
        </div>
      </div>

      {/* Colonne AVAL */}
      <div className="flex flex-col min-h-0">
        <div className="px-3 py-2.5 border-b border-white/5 bg-white/[0.02] flex items-center gap-1.5">
          <ArrowDownToLine className="w-3 h-3 text-[#1D9E75]" />
          <span className="text-[10px] uppercase tracking-wider text-[#1D9E75] font-bold">
            {t("chain.table_downstream", { defaultValue: "Aval · Les comment" })}
          </span>
          <span className="ml-auto text-[10px] text-zinc-500">{downstream.length}</span>
        </div>
        <div className="flex-1 overflow-y-auto p-2 space-y-1.5" data-testid="chain-table-downstream">
          {downstream.length === 0 ? (
            <div className="text-[11px] text-zinc-600 px-2 py-3 text-center italic">
              {t("chain.table_no_downstream", { defaultValue: "Aucun aval — ce nœud est une feuille." })}
            </div>
          ) : (
            downstream.map((d, i) => (
              <TableNodeCard
                key={`dn-${d.node.id}-${i}`}
                node={d.node}
                depth={d.depth}
                link={d.link}
                onClick={() => setFocalNodeId(d.node.id)}
                market={nodeMarkets[d.node.id]}
                busy={marketBusy.has(d.node.id)}
                onComputeMarket={() => onComputeMarket(d.node.id)}
                t={t}
              />
            ))
          )}
        </div>
      </div>
    </div>
  );
}

function TableNodeCard({ node, depth, link, onClick, market, busy, onComputeMarket, t }) {
  if (!node) return null;
  const color = TYPE_COLORS[node.type] || "#378ADD";
  const strength = link?.strength || 5;
  const strengthColor = edgeColorByStrength(strength);
  return (
    <div
      className="rounded-lg border bg-[#0d1626] hover:border-[#378ADD]/40 transition cursor-pointer group"
      style={{ borderColor: `${color}40`, marginLeft: Math.min(depth - 1, 3) * 12 }}
      onClick={onClick}
      data-testid={`chain-table-card-${node.id}`}
    >
      <div className="p-2.5">
        <div className="flex items-center justify-between gap-1.5 mb-1 text-[9px] uppercase tracking-wider font-bold">
          <span style={{ color }}>{TYPE_LABELS_FR[node.type] || node.type}</span>
          <span className="px-1 py-0.5 rounded bg-white/5 text-zinc-400">N{node.level}</span>
        </div>
        <div className="text-[12px] text-white font-medium leading-snug">{node.title}</div>
        <div className="flex items-center gap-1.5 mt-1.5 text-[10px]">
          <span className="text-zinc-500">{t("chain.table_link_strength", { defaultValue: "Force lien" })}</span>
          <span className="px-1.5 py-0.5 rounded font-semibold" style={{ background: `${strengthColor}22`, color: strengthColor }}>
            {strength}/10
          </span>
          {depth > 1 && (
            <span className="ml-auto text-zinc-600 text-[9px]">prof. {depth}</span>
          )}
        </div>
        {market?.is_market_relevant && market?.local?.value_meur != null && (
          <div className="mt-2 pt-1.5 border-t border-white/5 text-[10px] leading-tight">
            <div className="flex items-center gap-1 text-zinc-200">
              <BarChart3 className="w-2.5 h-2.5 text-[#EF9F27]" />
              <span className="font-semibold">{fmtMeur(market.local.value_meur)} {market.currency_primary}</span>
              <span className="text-zinc-500">· {market.local.label}</span>
            </div>
            {market.capture_pct != null && (
              <div className="text-zinc-400 mt-0.5">
                {market.capture_pct}% → <span className="text-[#1D9E75] font-semibold">{fmtMeur(market.capture_value_meur_annual)} {market.currency_primary}/an</span>
              </div>
            )}
          </div>
        )}
        {!market && (
          <button
            onClick={(e) => { e.stopPropagation(); onComputeMarket(); }}
            disabled={busy}
            className="mt-2 w-full text-[10px] py-1 rounded border border-white/10 text-zinc-400 hover:text-white hover:border-[#EF9F27]/40 inline-flex items-center justify-center gap-1 disabled:opacity-50"
            data-testid={`chain-table-card-market-btn-${node.id}`}
          >
            {busy ? <Loader2 className="w-2.5 h-2.5 animate-spin" /> : <BarChart3 className="w-2.5 h-2.5" />}
            {busy
              ? t("chain.market_estimating", { defaultValue: "Estimation…" })
              : t("chain.market_compute_short", { defaultValue: "Taille marché" })}
          </button>
        )}
      </div>
    </div>
  );
}

function FocalNodeCard({ node, market, busy, onComputeMarket, onForceRefresh, t }) {
  if (!node) return null;
  const color = TYPE_COLORS[node.type] || "#378ADD";
  return (
    <div
      className="w-full max-w-md rounded-xl border-2 shadow-xl"
      style={{ borderColor: color, background: `linear-gradient(135deg, ${color}22 0%, transparent 100%)` }}
      data-testid={`chain-table-focal-${node.id}`}
    >
      <div className="p-4">
        <div className="flex items-center justify-between gap-2 mb-2 text-[10px] uppercase tracking-wider font-bold">
          <span style={{ color }}>{TYPE_LABELS_FR[node.type] || node.type} · {node.direction === "up" ? "Pourquoi ↑" : "Comment ↓"}</span>
          <span className="px-1.5 py-0.5 rounded bg-white/10 text-white">N{node.level}</span>
        </div>
        <h2 className="text-base text-white font-bold leading-tight">{node.title}</h2>
        {node.description && (
          <p className="text-[11px] text-zinc-400 mt-1.5 leading-relaxed">{node.description}</p>
        )}
        <div className="mt-4 pt-3 border-t border-white/10 space-y-2.5">
          <div className="flex items-center justify-between">
            <div className="text-[10px] uppercase tracking-wider text-[#EF9F27] font-bold inline-flex items-center gap-1">
              <BarChart3 className="w-3 h-3" /> {t("chain.market_overline", { defaultValue: "Taille de marché" })}
            </div>
            {market && (
              <button onClick={onForceRefresh} disabled={busy} className="text-[10px] text-zinc-500 hover:text-white inline-flex items-center gap-1" data-testid="chain-table-focal-market-refresh">
                {busy ? <Loader2 className="w-2.5 h-2.5 animate-spin" /> : <RefreshCw className="w-2.5 h-2.5" />}
                {t("chain.market_refresh", { defaultValue: "Rafraîchir" })}
              </button>
            )}
          </div>
          {!market ? (
            <button
              onClick={onComputeMarket}
              disabled={busy}
              className="w-full text-[11px] py-2 rounded border border-[#EF9F27]/40 text-[#EF9F27] hover:bg-[#EF9F27]/10 inline-flex items-center justify-center gap-1.5 disabled:opacity-50"
              data-testid="chain-table-focal-market-btn"
            >
              {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
              {busy
                ? t("chain.market_estimating_long", { defaultValue: "Estimation Tavily + NOAM…" })
                : t("chain.market_compute", { defaultValue: "Estimer la taille de marché" })}
            </button>
          ) : market.is_market_relevant ? (
            <>
              <div className="grid grid-cols-2 gap-2 text-[11px]">
                <div className="bg-white/5 rounded-md p-2">
                  <div className="text-[9px] text-zinc-500 uppercase">{market.local?.label || "Local"}</div>
                  <div className="text-white font-bold text-sm mt-0.5">{fmtMeur(market.local?.value_meur)} {market.currency_primary}</div>
                </div>
                <div className="bg-white/5 rounded-md p-2">
                  <div className="text-[9px] text-zinc-500 uppercase">{market.world?.label || "Monde"}</div>
                  <div className="text-white font-bold text-sm mt-0.5">{fmtMeur(market.world?.value_meur)} {market.world?.value_meur != null ? (market.currency_primary === "CHF" ? "CHF" : "USD") : ""}</div>
                </div>
              </div>
              {market.capture_pct != null && (
                <div className="bg-[#1D9E75]/10 border border-[#1D9E75]/30 rounded-md p-2.5">
                  <div className="flex items-center gap-1 text-[10px] text-[#1D9E75] uppercase font-bold mb-1">
                    <Target className="w-3 h-3" /> {t("chain.market_capture", { defaultValue: "Capture estimée" })}
                  </div>
                  <div className="text-white text-sm">
                    <span className="font-bold">{market.capture_pct}%</span>
                    <span className="text-zinc-500 mx-1.5">→</span>
                    <span className="font-bold text-[#1D9E75]">{fmtMeur(market.capture_value_meur_annual)} {market.currency_primary}/an</span>
                  </div>
                </div>
              )}
              {market.narrative && (
                <p className="text-[11px] text-zinc-300 leading-relaxed italic border-l-2 border-[#EF9F27]/40 pl-2">
                  {market.narrative}
                </p>
              )}
              {market.computed_at && (
                <div className="text-[9px] text-zinc-600">
                  {t("chain.market_at", { defaultValue: "Calculé le" })} {new Date(market.computed_at).toLocaleString()}
                </div>
              )}
            </>
          ) : (
            <div className="text-[11px] text-zinc-500 italic leading-relaxed">
              {market.narrative || t("chain.market_not_relevant", { defaultValue: "Ce nœud n'est pas chiffrable en taille de marché." })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
